import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import {
  useAddCandidates,
  useBulkUpdateCandidates,
  useConvertCandidate,
  useCreateTrial,
  useCreateTrialSession,
  useDeleteTrial,
  useSaveObservations,
  useTrial,
  useTrials,
  useUpdateCandidate,
  useUpdateTrial,
  type CandidateRow,
} from '@/app/queries';
import { useAuthForm } from '@/routes/auth/useAuthForm';
import { formatDate, sheetName } from '@/lib/format';
import { splitFullName } from '@/lib/importer';
import { parseBirthDate } from '@shared/birthdate';
import { todayISO } from '@shared/dates';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/ui/Field';
import { Matrix } from '@/ui/Matrix';
import { EmptyState, Notice, Panel } from '@/ui/Panel';
import { Section } from '@/ui/Section';
import { Sheet, SheetHead, SheetRow } from '@/ui/Sheet';
import { Chip } from '@/ui/Chip';
import { BulkBar, RowCheck, useSelection } from '@/ui/Selection';

const ATTENDANCE = ['present', 'late', 'absent', 'injured'] as const;
const CALLS = ['keep', 'maybe', 'release'] as const;
const DIMENSIONS = [
  ['technicalScore', 'technical'],
  ['tacticalScore', 'tactical'],
  ['physicalScore', 'physical'],
  ['mentalScore', 'mental'],
  ['disciplineScore', 'discipline'],
  ['coachabilityScore', 'coachability'],
] as const;

type ScoreField = (typeof DIMENSIONS)[number][0];

const standingTone: Record<string, string> = {
  keep: 'text-ok',
  maybe: 'text-ink-muted',
  release: 'text-risk',
  look_again: 'text-signal',
  not_seen: 'text-ink-faint',
  no_call: 'text-ink-faint',
};

/**
 * Reads a pasted list into candidates.
 *
 * A club is handed its trialists as a list, not a form. One per line, with
 * anything after a comma treated as position and bib — which is how those lists
 * are actually written.
 */
function parseCandidateList(text: string) {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [namePart, ...rest] = line.split(/[,;\t]/);
      const name = splitFullName((namePart ?? '').trim());
      if (!name) return null;
      let position: string | undefined;
      let bib: number | undefined;
      let born: string | undefined;
      for (const piece of rest.map((r) => r.trim()).filter(Boolean)) {
        const asNumber = Number(piece);
        const asDate = parseBirthDate(piece);
        if (Number.isInteger(asNumber) && asNumber > 0 && asNumber < 1000 && piece.length <= 3) {
          bib = asNumber;
        } else if (asDate) {
          born = asDate;
        } else if (piece.length <= 4) {
          position = piece.toUpperCase();
        }
      }
      return {
        firstName: name.firstName,
        lastName: name.lastName,
        primaryPositionKey: position ?? null,
        bibNumber: bib ?? null,
        dateOfBirth: born ?? null,
      };
    })
    .filter((c): c is NonNullable<typeof c> => c !== null);
}

export function Trials({ locale, teamId }: { locale: Locale; teamId: string }) {
  const t = useTranslations();
  const list = useTrials(teamId);
  const create = useCreateTrial();
  const update = useUpdateTrial();
  const remove = useDeleteTrial();
  const { busy, formError, fieldError, submit } = useAuthForm();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [startsOn, setStartsOn] = useState(todayISO());
  const [endsOn, setEndsOn] = useState('');
  const [location, setLocation] = useState('');
  const [criteria, setCriteria] = useState('');
  const [status, setStatus] = useState('planning');

  const detail = useTrial(selectedId);
  const addCandidates = useAddCandidates(selectedId ?? '');
  const updateCandidate = useUpdateCandidate();
  const convert = useConvertCandidate();
  const createSession = useCreateTrialSession(selectedId ?? '');

  const [paste, setPaste] = useState('');
  const [sessionName, setSessionName] = useState('');
  const [sessionDate, setSessionDate] = useState(todayISO());
  const [sessionFocus, setSessionFocus] = useState('');
  const [activeSession, setActiveSession] = useState<string | null>(null);
  const [sheet, setSheet] = useState<Record<string, Record<string, unknown>>>({});
  const [decisionFor, setDecisionFor] = useState<string | null>(null);
  const [decision, setDecision] = useState('');
  const [decisionReason, setDecisionReason] = useState('');
  const [decisionError, setDecisionError] = useState<string | null>(null);

  const saveObservations = useSaveObservations(activeSession ?? '');
  const bulkCandidates = useBulkUpdateCandidates();
  const [bulkDecision, setBulkDecision] = useState('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkError, setBulkError] = useState<string | null>(null);

  const rows = list.data ?? [];
  const trial = detail.data;

  useEffect(() => {
    if (!trial || !activeSession) return;
    const next: Record<string, Record<string, unknown>> = {};
    for (const o of trial.observations.filter((x) => x.sessionId === activeSession)) {
      next[o.candidateId] = {
        attendance: o.attendance,
        positionPlayed: o.positionPlayed ?? '',
        technicalScore: o.technicalScore,
        tacticalScore: o.tacticalScore,
        physicalScore: o.physicalScore,
        mentalScore: o.mentalScore,
        disciplineScore: o.disciplineScore,
        coachabilityScore: o.coachabilityScore,
        note: o.note ?? '',
        recommendation: o.recommendation ?? '',
      };
    }
    setSheet(next);
  }, [trial, activeSession]);

  const startCreate = () => {
    setEditingId(null);
    setName('');
    setStartsOn(todayISO());
    setEndsOn('');
    setLocation('');
    setCriteria('');
    setStatus('planning');
    setOpenForm(true);
  };

  const startEdit = (row: (typeof rows)[number]) => {
    setEditingId(row.id);
    setName(row.name);
    setStartsOn(row.startsOn);
    setEndsOn(row.endsOn ?? '');
    setLocation(row.location ?? '');
    setCriteria(row.criteria ?? '');
    setStatus(row.status);
    setOpenForm(true);
  };

  const saveTrial = async () => {
    const input = {
      name,
      startsOn,
      endsOn: endsOn || null,
      location: location || null,
      criteria: criteria || null,
      status,
    };
    const ok = await submit(async () => {
      if (editingId) {
        await update.mutateAsync({ id: editingId, input });
      } else {
        const created = await create.mutateAsync({ ...input, teamId });
        setSelectedId(created.id);
      }
    });
    if (ok) setOpenForm(false);
  };

  const commitObservations = async () => {
    if (!activeSession || !trial) return;
    const payload = trial.candidates.map((c) => {
      const row = sheet[c.id] ?? {};
      const score = (f: ScoreField) => {
        const v = row[f];
        return v === '' || v === undefined || v === null ? null : Number(v);
      };
      return {
        candidateId: c.id,
        attendance: (row.attendance as string) || 'present',
        positionPlayed: (row.positionPlayed as string) || null,
        technicalScore: score('technicalScore'),
        tacticalScore: score('tacticalScore'),
        physicalScore: score('physicalScore'),
        mentalScore: score('mentalScore'),
        disciplineScore: score('disciplineScore'),
        coachabilityScore: score('coachabilityScore'),
        note: (row.note as string) || null,
        recommendation: (row.recommendation as string) || null,
      };
    });
    await saveObservations.mutateAsync(payload);
  };

  const saveDecision = async (candidate: CandidateRow) => {
    if (decision && !decisionReason.trim()) {
      setDecisionError(t('trial.decisionNeedsReason'));
      return;
    }
    setDecisionError(null);
    await updateCandidate.mutateAsync({
      id: candidate.id,
      input: {
        firstName: candidate.firstName,
        lastName: candidate.lastName,
        knownAs: candidate.knownAs,
        dateOfBirth: candidate.dateOfBirth,
        currentClub: candidate.currentClub,
        primaryPositionKey: candidate.primaryPositionKey,
        strongFoot: candidate.strongFoot,
        bibNumber: candidate.bibNumber,
        status: decision === 'selected' ? 'selected' : decision === 'not_selected' ? 'not_selected' : candidate.status,
        decision: decision || null,
        decisionReason: decisionReason || null,
        decidedOn: decision ? todayISO() : null,
        notes: candidate.notes,
      },
    });
    setDecisionFor(null);
    setDecision('');
    setDecisionReason('');
  };

  const parsed = useMemo(() => parseCandidateList(paste), [paste]);
  const selection = useSelection(
    useMemo(() => (trial?.candidates ?? []).map((c) => c.id), [trial?.candidates]),
  );

  return (
    <div className="grid gap-5">
      {openForm ? (
        <Panel
          title={editingId ? t('trial.edit') : t('trial.add')}
          action={
            <Button variant="quiet" onClick={() => setOpenForm(false)}>
              {t('common.cancel')}
            </Button>
          }
        >
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void saveTrial();
            }}
          >
            {formError ? (
              <div className="px-4 pt-4">
                <Notice tone="risk">{formError}</Notice>
              </div>
            ) : null}
            <div className="grid gap-x-5 gap-y-2 p-4 sm:grid-cols-2 lg:grid-cols-4">
              <Field label={t('trial.name')} error={fieldError('name')}>
                {(p) => (
                  <TextInput {...p} value={name} onChange={(e) => setName(e.target.value)} required />
                )}
              </Field>
              <Field label={t('trial.startsOn')} error={fieldError('startsOn')}>
                {(p) => (
                  <TextInput
                    {...p}
                    type="date"
                    value={startsOn}
                    onChange={(e) => setStartsOn(e.target.value)}
                    required
                  />
                )}
              </Field>
              <Field label={t('trial.endsOn')} optional>
                {(p) => (
                  <TextInput
                    {...p}
                    type="date"
                    value={endsOn}
                    onChange={(e) => setEndsOn(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('trial.location')} optional>
                {(p) => (
                  <TextInput
                    {...p}
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  />
                )}
              </Field>
              <div className="sm:col-span-2 lg:col-span-3">
                <Field label={t('trial.criteria')} optional hint={t('trial.criteriaHint')}>
                  {(p) => (
                    <TextArea
                      {...p}
                      rows={2}
                      value={criteria}
                      onChange={(e) => setCriteria(e.target.value)}
                    />
                  )}
                </Field>
              </div>
              <Field label={t('trial.statusLabel')} optional>
                {(p) => (
                  <SelectInput {...p} value={status} onChange={(e) => setStatus(e.target.value)}>
                    {['planning', 'running', 'closed'].map((s) => (
                      <option key={s} value={s}>
                        {t(`trial.status.${s}`)}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>
            </div>
            <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-4">
              <Button type="submit" variant="primary" busy={busy} disabled={!name}>
                {t('trial.save')}
              </Button>
              {editingId ? (
                <Button
                  variant="danger"
                  onClick={() => {
                    if (!window.confirm(t('trial.confirmDelete'))) return;
                    void remove.mutateAsync(editingId).then(() => {
                      setOpenForm(false);
                      if (selectedId === editingId) setSelectedId(null);
                    });
                  }}
                >
                  {t('trial.delete')}
                </Button>
              ) : null}
            </div>
          </form>
        </Panel>
      ) : null}

      <Panel
        title={t('trial.title')}
        count={rows.length}
        action={
          openForm ? null : (
            <Button variant="primary" onClick={startCreate}>
              {t('trial.add')}
            </Button>
          )
        }
      >
        {list.isLoading ? (
          <p className="flex items-center gap-2 px-4 py-6 text-ink-muted">
            <Spinner /> {t('common.loading')}
          </p>
        ) : rows.length === 0 ? (
          <EmptyState
            title={t('trial.empty')}
            body={t('trial.emptyBody')}
            action={
              <Button variant="primary" onClick={startCreate}>
                {t('trial.add')}
              </Button>
            }
          />
        ) : (
          <>
            <SheetHead>
              <span className="w-24 shrink-0">{t('trial.startsOn')}</span>
              <span className="flex-1">{t('trial.name')}</span>
              <span className="hidden w-40 shrink-0 text-end sm:block">
                {t('trial.candidatesLabel')}
              </span>
            </SheetHead>
            <Sheet>
              {rows.map((r) => (
                <SheetRow
                  key={r.id}
                  highlight={r.id === selectedId}
                  onClick={() => setSelectedId(r.id === selectedId ? null : r.id)}
                >
                  <span className="tabular w-24 shrink-0 text-meta text-ink-muted">
                    {formatDate(r.startsOn, locale)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{r.name}</span>
                    <span className="block truncate text-meta text-ink-faint">
                      {t(`trial.status.${r.status}`)}
                      {r.location ? ` · ${r.location}` : ''}
                    </span>
                  </span>
                  <span className="hidden w-40 shrink-0 justify-end gap-2 text-meta text-ink-muted sm:flex">
                    <span>{t('trial.candidates', { count: r.candidates })}</span>
                    <span className="text-ink-faint">
                      {t('trial.sessions', { count: r.sessions })}
                    </span>
                  </span>
                </SheetRow>
              ))}
            </Sheet>
          </>
        )}
      </Panel>

      {selectedId && trial ? (
        <>
          <Panel
            title={trial.trial.name}
            count={formatDate(trial.trial.startsOn, locale)}
            action={
              <Button
                variant="quiet"
                onClick={() => {
                  const row = rows.find((r) => r.id === selectedId);
                  if (row) startEdit(row);
                }}
              >
                {t('trial.edit')}
              </Button>
            }
          >
            {trial.trial.criteria ? (
              <div className="border-b border-line px-4 py-3">
                <p className="text-meta text-ink-faint">{t('trial.criteria')}</p>
                <p className="max-w-[80ch]">{trial.trial.criteria}</p>
              </div>
            ) : null}

            <Section title={t('trial.addCandidates')} hint={t('trial.addCandidatesHint')}>
              <div className="grid gap-2">
                <TextArea
                  rows={3}
                  value={paste}
                  placeholder={'Youssef Benali, CB, 4\nOmar Tazi, GK, 1'}
                  onChange={(e) => setPaste(e.target.value)}
                />
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="secondary"
                    busy={addCandidates.isPending}
                    disabled={parsed.length === 0}
                    onClick={() =>
                      void addCandidates.mutateAsync(parsed).then(() => setPaste(''))
                    }
                  >
                    {t('trial.addCandidates')}
                  </Button>
                  {paste.trim() ? (
                    <span className="text-meta text-ink-faint">
                      {t('trial.candidates', { count: parsed.length })}
                    </span>
                  ) : null}
                </div>
              </div>
            </Section>

            <Section title={t('trial.addSession')}>
              <div className="grid gap-x-5 gap-y-2 sm:grid-cols-4">
                <Field label={t('trial.sessionName')}>
                  {(p) => (
                    <TextInput
                      {...p}
                      value={sessionName}
                      onChange={(e) => setSessionName(e.target.value)}
                    />
                  )}
                </Field>
                <Field label={t('trial.startsOn')}>
                  {(p) => (
                    <TextInput
                      {...p}
                      type="date"
                      value={sessionDate}
                      onChange={(e) => setSessionDate(e.target.value)}
                    />
                  )}
                </Field>
                <div className="sm:col-span-2">
                  <Field label={t('trial.focus')} optional>
                    {(p) => (
                      <TextInput
                        {...p}
                        value={sessionFocus}
                        onChange={(e) => setSessionFocus(e.target.value)}
                      />
                    )}
                  </Field>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="secondary"
                  busy={createSession.isPending}
                  disabled={!sessionName}
                  onClick={() =>
                    void createSession
                      .mutateAsync({
                        name: sessionName,
                        onDate: sessionDate,
                        focus: sessionFocus || null,
                      })
                      .then((created) => {
                        setActiveSession(created.id);
                        setSessionName('');
                        setSessionFocus('');
                      })
                  }
                >
                  {t('trial.addSession')}
                </Button>
                {trial.sessions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    aria-pressed={s.id === activeSession}
                    onClick={() => setActiveSession(s.id === activeSession ? null : s.id)}
                    className={`rounded-(--radius-control) border px-2.5 py-1 text-meta transition-colors ${
                      s.id === activeSession
                        ? 'border-ink bg-ink text-paper'
                        : 'border-line bg-surface text-ink-muted hover:border-ink hover:text-ink'
                    }`}
                  >
                    {s.name}
                    <span className="ms-2 opacity-70">{formatDate(s.onDate, locale)}</span>
                  </button>
                ))}
              </div>
            </Section>
          </Panel>

          {activeSession && trial.candidates.length > 0 ? (
            <Panel
              title={t('trial.observations')}
              count={trial.sessions.find((s) => s.id === activeSession)?.name}
              action={
                <Button
                  variant="primary"
                  busy={saveObservations.isPending}
                  onClick={() => void commitObservations()}
                >
                  {t('trial.saveObservations')}
                </Button>
              }
            >
              {/*
                The widest table in the product at 832px: candidate, attendance,
                one column per scoring dimension, and a recommendation — six
                dimensions today and however many a club adds later. A card per
                candidate is the only shape that survives that, and an
                observation sheet is filled in one person at a time anyway.
              */}
              <Matrix
                rows={trial.candidates}
                rowKey={(c: (typeof trial.candidates)[number]) => c.id}
                caption={t('trial.candidate')}
                columns={[
                  {
                    key: 'candidate',
                    label: t('trial.candidate'),
                    heading: true,
                    cell: (c: (typeof trial.candidates)[number]) => (
                      <span className="flex items-baseline gap-2">
                        <span className="tabular text-ink-faint">{c.bibNumber ?? '–'}</span>
                        {sheetName(c.knownAs ?? c.firstName, c.lastName)}
                      </span>
                    ),
                  },
                  {
                    key: 'attendance',
                    label: t('trial.attendance'),
                    cell: (c: (typeof trial.candidates)[number]) => (
                      <SelectInput
                        aria-label={t('trial.attendance')}
                        className="w-full sm:w-24"
                        value={((sheet[c.id] ?? {}).attendance as string) ?? 'present'}
                        onChange={(e) =>
                          setSheet((s) => ({
                            ...s,
                            [c.id]: { ...(s[c.id] ?? {}), attendance: e.target.value },
                          }))
                        }
                      >
                        {ATTENDANCE.map((a) => (
                          <option key={a} value={a}>
                            {t(`trial.attendanceValue.${a}`)}
                          </option>
                        ))}
                      </SelectInput>
                    ),
                  },
                  ...DIMENSIONS.map(([field, key]) => ({
                    key,
                    label:
                      t(`development.dimension.${key}`) === `development.dimension.${key}`
                        ? t(`trial.${key}`)
                        : t(`development.dimension.${key}`),
                    cell: (c: (typeof trial.candidates)[number]) => {
                      const row = sheet[c.id] ?? {};
                      return (
                        <SelectInput
                          aria-label={field}
                          className="w-full sm:w-16"
                          value={
                            row[field] === null || row[field] === undefined
                              ? ''
                              : String(row[field])
                          }
                          onChange={(e) =>
                            setSheet((s) => ({
                              ...s,
                              [c.id]: {
                                ...(s[c.id] ?? {}),
                                [field]: e.target.value === '' ? null : Number(e.target.value),
                              },
                            }))
                          }
                        >
                          <option value="">—</option>
                          {[1, 2, 3, 4, 5].map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </SelectInput>
                      );
                    },
                  })),
                  {
                    key: 'recommendation',
                    label: t('trial.recommendation'),
                    cell: (c: (typeof trial.candidates)[number]) => (
                      <SelectInput
                        aria-label={t('trial.recommendation')}
                        className="w-full sm:w-28"
                        value={((sheet[c.id] ?? {}).recommendation as string) ?? ''}
                        onChange={(e) =>
                          setSheet((s) => ({
                            ...s,
                            [c.id]: { ...(s[c.id] ?? {}), recommendation: e.target.value },
                          }))
                        }
                      >
                        <option value="">{t('trial.noCall')}</option>
                        {CALLS.map((r) => (
                          <option key={r} value={r}>
                            {t(`trial.rec.${r}`)}
                          </option>
                        ))}
                      </SelectInput>
                    ),
                  },
                ]}
              />
            </Panel>
          ) : null}

          <Panel title={t('trial.candidatesLabel')} count={trial.candidates.length}>
            {trial.candidates.length === 0 ? (
              <EmptyState title={t('trial.noCandidates')} body={t('trial.noCandidatesBody')} />
            ) : (
              <Sheet>
                {trial.candidates.map((c) => (
                  <li key={c.id} className="px-4 py-3">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <RowCheck
                        checked={selection.has(c.id)}
                        onChange={() => selection.toggle(c.id)}
                        label={t('bulk.selectRow', {
                          name: sheetName(c.knownAs ?? c.firstName, c.lastName),
                        })}
                      />
                      <span className="tabular w-8 shrink-0 font-condensed text-subhead font-semibold text-ink-muted">
                        {c.bibNumber ?? '–'}
                      </span>
                      <span className="font-medium">
                        {sheetName(c.knownAs ?? c.firstName, c.lastName)}
                      </span>
                      {c.primaryPositionKey ? (
                        <span className="tabular font-condensed text-meta text-ink-faint">
                          {c.primaryPositionKey}
                        </span>
                      ) : null}
                      {c.currentClub ? (
                        <span className="text-meta text-ink-faint">{c.currentClub}</span>
                      ) : null}
                      <span className="ms-auto flex items-center gap-2">
                        {c.playerId ? <Chip>{t('trial.converted')}</Chip> : null}
                        <span className={`text-meta ${standingTone[c.standing.standing] ?? ''}`}>
                          {t(`trial.standingValue.${c.standing.standing}`)}
                        </span>
                      </span>
                    </div>

                    <p className="mt-1 text-meta text-ink-muted">
                      {t('trial.seen', { count: c.summary.attended })}
                      {c.summary.missed > 0
                        ? ` · ${t('trial.attendanceValue.absent')} ${c.summary.missed}`
                        : ''}
                      {c.summary.overall !== null
                        ? ` · ${t('trial.overall')} ${c.summary.overall.toFixed(1)}`
                        : ''}
                      {c.summary.spread !== null && c.summary.spread > 0
                        ? ` · ${t('trial.spread', { value: c.summary.spread.toFixed(1) })}`
                        : ''}
                    </p>
                    <p className="text-meta text-ink-faint">
                      {t(c.standing.reason)}
                      {c.standing.signals.length > 0
                        ? ` — ${c.standing.signals.map((s) => t(s)).join(' · ')}`
                        : ''}
                    </p>

                    {c.decision ? (
                      <p className="mt-1 text-meta">
                        <span className="text-ink-faint">{t('trial.decision')}: </span>
                        {c.decision}
                        {c.decisionReason ? ` — ${c.decisionReason}` : ''}
                      </p>
                    ) : null}

                    {decisionFor === c.id ? (
                      <div className="mt-2 grid gap-2 sm:grid-cols-[10rem_1fr_auto] sm:items-end">
                        <Field label={t('trial.decision')}>
                          {(p) => (
                            <SelectInput
                              {...p}
                              value={decision}
                              onChange={(e) => setDecision(e.target.value)}
                            >
                              <option value="">{t('trial.noCall')}</option>
                              <option value="selected">{t('trial.rec.keep')}</option>
                              <option value="reserve">{t('trial.standingValue.maybe')}</option>
                              <option value="not_selected">{t('trial.rec.release')}</option>
                            </SelectInput>
                          )}
                        </Field>
                        <Field label={t('trial.decisionReason')} error={decisionError ?? undefined}>
                          {(p) => (
                            <TextInput
                              {...p}
                              value={decisionReason}
                              onChange={(e) => setDecisionReason(e.target.value)}
                            />
                          )}
                        </Field>
                        <div className="pb-2">
                          <Button variant="primary" onClick={() => void saveDecision(c)}>
                            {t('common.save')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button
                          variant="quiet"
                          onClick={() => {
                            setDecisionFor(c.id);
                            setDecision(c.decision ?? '');
                            setDecisionReason(c.decisionReason ?? '');
                            setDecisionError(null);
                          }}
                        >
                          {t('trial.decision')}
                        </Button>
                        {!c.playerId ? (
                          <Button
                            variant="secondary"
                            busy={convert.isPending}
                            onClick={() =>
                              void convert.mutateAsync({ id: c.id, input: { teamId } })
                            }
                          >
                            {t('trial.convert')}
                          </Button>
                        ) : null}
                      </div>
                    )}
                  </li>
                ))}
              </Sheet>
            )}
          </Panel>

          <BulkBar count={selection.count} onClear={selection.clear}>
            <SelectInput
              aria-label={t('trial.decision')}
              className="w-40"
              value={bulkDecision}
              onChange={(e) => {
                setBulkDecision(e.target.value);
                setBulkError(null);
              }}
            >
              <option value="">{t('trial.noCall')}</option>
              <option value="selected">{t('trial.rec.keep')}</option>
              <option value="reserve">{t('trial.standingValue.maybe')}</option>
              <option value="not_selected">{t('trial.rec.release')}</option>
            </SelectInput>

            <TextInput
              aria-label={t('trial.decisionReason')}
              className="w-56"
              placeholder={t('trial.decisionReason')}
              value={bulkReason}
              onChange={(e) => {
                setBulkReason(e.target.value);
                setBulkError(null);
              }}
            />

            <Button
              variant="primary"
              busy={bulkCandidates.isPending}
              disabled={!bulkDecision}
              onClick={() => {
                // Shared across the selection, and still required — a group
                // decision usually does have one shared reason.
                if (!bulkReason.trim()) {
                  setBulkError(t('trial.decisionNeedsReason'));
                  return;
                }
                void bulkCandidates
                  .mutateAsync({
                    candidateIds: [...selection.selected],
                    status:
                      bulkDecision === 'selected'
                        ? 'selected'
                        : bulkDecision === 'not_selected'
                          ? 'not_selected'
                          : 'reserve',
                    decision: bulkDecision,
                    decisionReason: bulkReason.trim(),
                  })
                  .then(() => {
                    selection.clear();
                    setBulkDecision('');
                    setBulkReason('');
                  });
              }}
            >
              {t('bulk.apply')}
            </Button>

            {bulkError ? <span className="text-meta text-risk">{bulkError}</span> : null}
          </BulkBar>
        </>
      ) : null}
    </div>
  );
}
